/* ================================================================
   AuthContext.tsx — home-page auth state.

   The home page never holds a session of its own. It runs the same email/
   password flow the app uses (inline in the hero card — see AuthFlow), then
   hands the JWT to app.juristab.com via the URL fragment and navigates there.
   The app reads the fragment on load and writes the tokens into the exact same
   localStorage keys it would on a native login, so the session is shared
   identically.

   Context here is intentionally thin: the inline flow owns its own step state.
   We only expose the shared redirect (completeAuth), the login action reused for
   post-verification auto-login, and an "intent" channel so the navbar can ask
   the Home card to open in signup/signin mode.
   ================================================================ */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { APP_URL, APP_LANDING_PATH, HANDOFF_PARAMS } from '../config/authConfig';
import { AuthTokens, loginWithEmail } from '../services/authService';

export type AuthMode = 'signin' | 'signup';

interface AuthContextValue {
  /** Set when something (e.g. the navbar) asks the Home card to open in a mode. */
  authIntent: AuthMode | null;
  requestAuth: (mode: AuthMode) => void;
  clearAuthIntent: () => void;
  /** Email/password login, then hand off to the app. */
  loginWithEmailAction: (creds: { email: string; password: string }) => Promise<void>;
  /** Persist the JWT to the app's origin via URL fragment and navigate there. */
  completeAuth: (tokens: AuthTokens, email?: string) => void;
}

const HANDOFF_COOKIE_NAMES = {
  access: 'jt_handoff_access',
  id: 'jt_handoff_id',
  refresh: 'jt_handoff_refresh',
  email: 'jt_handoff_email',
} as const;

/** Short window — just long enough for the redirect to land and the app to read them. */
const HANDOFF_COOKIE_MAX_AGE_SECONDS = 60;

/** Return the shared parent domain (".example.com") if both hosts live under
 *  one. Returns null for IPs, single-label hosts (localhost), or when the
 *  hosts share only a TLD. */
function sharedParentDomain(hostA: string, hostB: string): string | null {
  const a = hostA.split(':')[0];
  const b = hostB.split(':')[0];
  if (!a || !b || a === b) return null;
  if (/^[\d.]+$/.test(a) || /^[\d.]+$/.test(b)) return null;
  const partsA = a.split('.');
  const partsB = b.split('.');
  if (partsA.length < 2 || partsB.length < 2) return null;
  let i = 0;
  while (i < Math.min(partsA.length, partsB.length)) {
    if (partsA[partsA.length - 1 - i] !== partsB[partsB.length - 1 - i]) break;
    i++;
  }
  if (i < 2) return null;
  return '.' + partsA.slice(partsA.length - i).join('.');
}

function setHandoffCookie(name: string, value: string, domain: string) {
  const encoded = encodeURIComponent(value);
  document.cookie =
    `${name}=${encoded}; Path=/; Domain=${domain}; Max-Age=${HANDOFF_COOKIE_MAX_AGE_SECONDS}; Secure; SameSite=Lax`;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authIntent, setAuthIntent] = useState<AuthMode | null>(null);

  const completeAuth = useCallback((tokens: AuthTokens, email?: string) => {
    const url = new URL(APP_URL);
    url.pathname = APP_LANDING_PATH;

    // Preferred: hand off via short-lived cookies on the shared parent
    // domain (e.g. .juristab.com). The redirect URL stays clean —
    // app.juristab.com/workflow#access_token=… was being blocked by Chrome
    // Safe Browsing as a credential-phishing pattern.
    const parent = sharedParentDomain(window.location.host, url.host);
    if (parent) {
      setHandoffCookie(HANDOFF_COOKIE_NAMES.access, tokens.token, parent);
      if (tokens.idToken) setHandoffCookie(HANDOFF_COOKIE_NAMES.id, tokens.idToken, parent);
      if (tokens.refreshToken) setHandoffCookie(HANDOFF_COOKIE_NAMES.refresh, tokens.refreshToken, parent);
      if (email) setHandoffCookie(HANDOFF_COOKIE_NAMES.email, email, parent);
      window.location.assign(url.toString());
      return;
    }

    // Dev / no shared parent domain (e.g. localhost ⇄ app.juristab.com):
    // fall back to the URL-fragment handoff that the app still accepts.
    const frag = new URLSearchParams();
    frag.set(HANDOFF_PARAMS.accessToken, tokens.token);
    if (tokens.idToken) frag.set(HANDOFF_PARAMS.idToken, tokens.idToken);
    if (tokens.refreshToken) frag.set(HANDOFF_PARAMS.refreshToken, tokens.refreshToken);
    if (email) frag.set(HANDOFF_PARAMS.email, email);
    url.hash = frag.toString();
    window.location.assign(url.toString());
  }, []);

  const loginWithEmailAction = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      const tokens = await loginWithEmail({ email, password });
      completeAuth(tokens, email);
    },
    [completeAuth]
  );

  const requestAuth = useCallback((mode: AuthMode) => setAuthIntent(mode), []);
  const clearAuthIntent = useCallback(() => setAuthIntent(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({ authIntent, requestAuth, clearAuthIntent, loginWithEmailAction, completeAuth }),
    [authIntent, requestAuth, clearAuthIntent, loginWithEmailAction, completeAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
